import React, { useMemo, useState } from 'react';
import { t } from 'i18next';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DataTable, useToast, Label, } from '@cargomatic/ui';
import { useRateConditions } from 'hooks/useLaneRate';
import { ArrowDownIcon, ArrowUpIcon, LoaderCircle } from 'lucide-react';
import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { updateRateSequence } from 'utils/AccessorialRequestWebAPIUtils';
import { getColumns } from './rate-condition-columns';
import { CustomerTypeBadge } from './CustomerTypeBadge';
import moment from 'moment';
const highlighted = {
    rateCustomer: true,
    rateMarket: true,
    rateEquipmentType: true,
    rateEquipmentSize: true,
    rateHazardous: true,
    rateOrderType: true,
    rateSteamshipOrRailroad: true,
};
export function EditRateSequenceModal({ laneRate, open = false, onOpenChange, onSaveSuccess, }) {
    const { laneId, customerId } = laneRate;
    const { toast } = useToast();
    const { data, isLoading } = useRateConditions({ customerId, laneId });
    const [isSaving, setIsSaving] = useState(false);
    const [rateSequenceList, setRateSequenceList] = useState([]);
    const columns = getColumns();
    const sequencedRates = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }
        if (rateSequenceList.length > 0) {
            return rateSequenceList;
        }
        return data;
    }, [data, rateSequenceList]);
    const lastIndex = useMemo(() => {
        if (isLoading || !data || data.length === 0) {
            return 0;
        }
        return data.length - 1;
    }, [data, isLoading]);
    function onSequenceRate({ index, dir }) {
        const newIndex = dir === 'up' ? index - 1 : index + 1;
        const currentItem = Object.assign({}, sequencedRates[index]);
        const itemToSwap = Object.assign({}, sequencedRates[newIndex]);
        const resequencedList = sequencedRates.map((row, rowIndex) => {
            if (rowIndex === index) {
                return itemToSwap;
            }
            if (rowIndex === newIndex) {
                return currentItem;
            }
            return row;
        });
        setRateSequenceList(resequencedList);
    }
    const handleUpdateRateSequence = async (laneId, customer, sequencedRates) => {
        const updatedSequenceDates = sequencedRates.map((laneRate, index) => {
            return {
                laneRateId: laneRate.laneRateId,
                sequenceDate: moment().add(index, 'seconds'),
            };
        });
        await updateRateSequence({
            laneId,
            customer,
            laneRates: updatedSequenceDates,
        });
    };
    const mappedColumnDefs = useMemo(() => {
        return [
            {
                id: 'sequence',
                header: 'Sequence',
                enableSorting: false,
                enableHiding: false,
                cell: (props) => {
                    const { row } = props;
                    const { index } = row;
                    const sequence = index + 1;
                    return (React.createElement("div", { className: "flex items-center space-x-2" },
                        React.createElement("span", null,
                            sequence,
                            "."),
                        React.createElement(Button, { variant: "outline", className: "ml-2 w-8 h-8", size: "icon", disabled: index === 0, onClick: () => onSequenceRate({ index, dir: 'up' }) },
                            React.createElement(ArrowUpIcon, { className: "w-4 h-4 flex-shrink-0" })),
                        React.createElement(Button, { variant: "outline", className: "w-8 h-8", size: "icon", disabled: index === lastIndex, onClick: () => onSequenceRate({ index, dir: 'down' }) },
                            React.createElement(ArrowDownIcon, { className: "w-4 h-4 flex-shrink-0" }))));
                },
            },
            ...columns,
        ];
    }, [sequencedRates]);
    const options = {
        columns: mappedColumnDefs,
        data: sequencedRates,
        enableRowSelection: false,
        getCoreRowModel: getCoreRowModel(),
    };
    const table = useReactTable(options);
    async function handleSaveRateSequences() {
        try {
            const { laneId, customerId: customer } = laneRate;
            setIsSaving(true);
            await handleUpdateRateSequence(laneId, customer, sequencedRates);
            toast({ description: 'Rate sequence updated!' });
            onSaveSuccess();
        }
        catch (e) {
            toast({ variant: 'destructive', description: e.message });
        }
        finally {
            setIsSaving(false);
        }
    }
    return (React.createElement(Dialog, { open: open, onOpenChange: onOpenChange },
        React.createElement(DialogContent, { className: "min-w-full" },
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, t('EDIT_RATE_SEQUENCE')),
                React.createElement(DialogDescription, null,
                    React.createElement("div", { className: "mb-4" }, t('EDIT_RATE_SEQUENCE_DESCRIPTION')),
                    React.createElement("div", { className: "flex space-x-16 items-center flex-wrap" },
                        React.createElement("div", { className: "flex space-x-4 items-center" },
                            React.createElement(Label, null, t('CUSTOMER_TYPE')),
                            React.createElement("dd", { className: "mt-1 text-sm leading-6 text-gray-700 sm:mt-0" },
                                React.createElement(CustomerTypeBadge, { customerType: laneRate === null || laneRate === void 0 ? void 0 : laneRate.customerType }))),
                        React.createElement("div", { className: "flex space-x-4 items-center" },
                            React.createElement(Label, null, t('CUSTOMER')),
                            React.createElement("dd", { className: "mt-1 text-sm leading-6 text-gray-700 sm:mt-0" }, (laneRate === null || laneRate === void 0 ? void 0 : laneRate.customerName) || 'Sabe Trucking')),
                        React.createElement("div", { className: "flex space-x-4 items-center" },
                            React.createElement(Label, null, t('ORIGIN')),
                            React.createElement("dd", { className: "mt-1 text-sm leading-6 text-gray-700 sm:mt-0" }, (laneRate === null || laneRate === void 0 ? void 0 : laneRate.origin) || 'Port of LA/LB')),
                        React.createElement("div", { className: "flex space-x-4 items-center" },
                            React.createElement(Label, null, t('DESTINATION')),
                            React.createElement("dd", { className: "mt-1 text-sm leading-6 text-gray-700 sm:mt-0" }, (laneRate === null || laneRate === void 0 ? void 0 : laneRate.destination) || '91422'))))),
            React.createElement("div", { className: "overflow-x-auto mb-4", style: { maxHeight: '60vh' } },
                React.createElement(DataTable, { table: table, isLoading: isLoading, highlighted: highlighted, skeletonRowCount: 5 })),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { variant: "outline", onClick: onOpenChange, disabled: isSaving }, t('CANCEL')),
                React.createElement(Button, { type: "submit", onClick: handleSaveRateSequences, disabled: isSaving },
                    React.createElement("span", null, t('SAVE_CHANGES')),
                    isSaving && React.createElement(LoaderCircle, { className: "w-4 h-4 animate-spin ml-2" }))))));
}
